import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Mail, Check, AlertCircle, Eye, EyeOff, Save, TestTube } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Integrations = () => {
  const { token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  
  const [resendConfig, setResendConfig] = useState({
    api_key: '',
    sender_email: '',
    app_url: '',
    app_name: '',
    is_active: true
  });
  const [isConfigured, setIsConfigured] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchIntegration();
  }, []);

  const fetchIntegration = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/integrations/resend`, { headers });
      if (response.data.configured) {
        setResendConfig({
          api_key: '', // Don't show actual key
          sender_email: response.data.sender_email || '',
          app_url: response.data.app_url || '',
          app_name: response.data.app_name || '',
          is_active: response.data.is_active !== false
        });
        setIsConfigured(true);
      }
    } catch (error) {
      console.error('Failed to fetch integration:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!resendConfig.api_key && !isConfigured) {
      toast.error('API Key is required');
      return;
    }
    if (!resendConfig.sender_email) {
      toast.error('Sender email is required');
      return;
    }
    if (!resendConfig.app_url) {
      toast.error('App URL is required');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/integrations`, {
        provider: 'resend',
        ...resendConfig,
        api_key: resendConfig.api_key || undefined // Don't send empty string
      }, { headers });
      
      toast.success('Resend configuration saved');
      setIsConfigured(true);
      // Clear the API key field after saving
      setResendConfig(prev => ({ ...prev, api_key: '' }));
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save configuration';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address for the test');
      return;
    }

    setTesting(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/integrations/resend/test?test_email=${encodeURIComponent(testEmail)}`,
        {},
        { headers }
      );
      toast.success(response.data.message || 'Test email sent successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Test failed';
      toast.error(message);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Integrations</h1>
        <p className="text-muted-foreground">Configure third-party service integrations</p>
      </div>

      {/* Resend Email Integration */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-zinc-100 dark:bg-zinc-800 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <CardTitle className="text-lg">Resend</CardTitle>
                <CardDescription>Email delivery for welcome emails, password resets, and notifications</CardDescription>
              </div>
            </div>
            {isConfigured ? (
              <div className="flex items-center gap-1 text-sm text-green-600">
                <Check className="w-4 h-4" />
                Configured
              </div>
            ) : (
              <div className="flex items-center gap-1 text-sm text-amber-600">
                <AlertCircle className="w-4 h-4" />
                Not configured
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="api_key">API Key {!isConfigured && <span className="text-red-500">*</span>}</Label>
              <div className="relative">
                <Input
                  id="api_key"
                  type={showApiKey ? 'text' : 'password'}
                  placeholder={isConfigured ? '••••••••••••••••' : 're_xxxxxxxxxx'}
                  value={resendConfig.api_key}
                  onChange={(e) => setResendConfig({ ...resendConfig, api_key: e.target.value })}
                  data-testid="resend-api-key"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowApiKey(!showApiKey)}
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                {isConfigured ? 'Leave blank to keep existing key' : 'Get from resend.com → API Keys'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sender_email">Sender Email <span className="text-red-500">*</span></Label>
              <Input
                id="sender_email"
                type="email"
                placeholder="noreply@yourdomain.com"
                value={resendConfig.sender_email}
                onChange={(e) => setResendConfig({ ...resendConfig, sender_email: e.target.value })}
                data-testid="resend-sender-email"
              />
              <p className="text-xs text-muted-foreground">
                Must be from a verified domain in Resend
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="app_url">App URL <span className="text-red-500">*</span></Label>
              <Input
                id="app_url"
                type="url"
                placeholder="https://yourapp.com"
                value={resendConfig.app_url}
                onChange={(e) => setResendConfig({ ...resendConfig, app_url: e.target.value })}
                data-testid="resend-app-url"
              />
              <p className="text-xs text-muted-foreground">
                Used for links in emails (password reset, etc.)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="app_name">App Name</Label>
              <Input
                id="app_name"
                type="text"
                placeholder="Sales Dashboard"
                value={resendConfig.app_name}
                onChange={(e) => setResendConfig({ ...resendConfig, app_name: e.target.value })}
                data-testid="resend-app-name"
              />
              <p className="text-xs text-muted-foreground">
                Appears in email subjects and content
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button onClick={handleSave} disabled={saving} data-testid="save-resend-config">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Configuration
                </>
              )}
            </Button>
          </div>

          {/* Test Connection */}
          {isConfigured && (
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-medium mb-3">Test Connection</h4>
              <div className="flex items-end gap-3">
                <div className="flex-1 space-y-2">
                  <Label htmlFor="test_email">Send test email to</Label>
                  <Input
                    id="test_email"
                    type="email"
                    placeholder="your@email.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    data-testid="test-email-input"
                  />
                </div>
                <Button variant="outline" onClick={handleTest} disabled={testing} data-testid="test-resend">
                  {testing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <TestTube className="w-4 h-4 mr-2" />
                      Test Connection
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Setup Instructions */}
          <div className="border-t pt-4 mt-4">
            <h4 className="text-sm font-medium mb-2">Setup Instructions</h4>
            <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
              <li>Sign up at <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">resend.com</a></li>
              <li>Create an API key in the dashboard</li>
              <li>Verify your sending domain (add DNS records)</li>
              <li>Enter your API key and sender email above</li>
              <li>Click "Test Connection" to verify</li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Integrations;
